import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, UserPlus, Users, UserCircle, Prohibit, CheckCircle,
  Spinner, X, MagnifyingGlass, CaretRight, Clock, Crown, CreditCard,
} from '@phosphor-icons/react';

const getActivityLabel = (lastLogin) => {
  if (!lastLogin) return { text: 'Never logged in', color: 'text-slate-300' };
  const now = new Date();
  const login = new Date(lastLogin);
  const diffMs = now - login;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;
  if (diffHours < 24) return { text: 'Active today', color: 'text-emerald-500' };
  if (diffDays < 7) return { text: 'Active this week', color: 'text-blue-500' };
  if (diffDays < 30) return { text: 'Active this month', color: 'text-amber-500' };
  return { text: 'Inactive', color: 'text-slate-400' };
};

const formatLastLogin = (lastLogin) => {
  if (!lastLogin) return null;
  const dt = new Date(lastLogin);
  const now = new Date();
  const diffMs = now - dt;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const ACCESS_BADGE = {
  'Founder/free':  { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200' },
  'Paid active':   { bg: 'bg-blue-50',     text: 'text-blue-700',    border: 'border-blue-200' },
  'Trial active':  { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200' },
  'Trial expired': { bg: 'bg-rose-50',     text: 'text-rose-700',    border: 'border-rose-200' },
  'No plan':       { bg: 'bg-slate-50',    text: 'text-slate-500',   border: 'border-slate-200' },
};

const formatTrialDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return null; }
};

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'user' });
  // Founder access controls
  const [grantingId, setGrantingId] = useState(null);  // user id currently being granted/revoked
  const [revokeConfirm, setRevokeConfirm] = useState(null);  // user object pending revoke confirm

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/home');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('All fields are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, form);
      toast.success(`User ${form.email} created`);
      setShowCreateModal(false);
      setForm({ email: '', name: '', password: '', role: 'user' });
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    setToggling(userId);
    try {
      const action = currentActive ? 'deactivate' : 'activate';
      await axios.post(`${API}/admin/users/${userId}/${action}`);
      toast.success(`User ${action}d`);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setToggling(null);
    }
  };

  // Stage 3 — founder access controls
  const handleGrantFounder = async (u) => {
    setGrantingId(u.id);
    try {
      await axios.post(`${API}/admin/users/${u.id}/founder/grant`, { note: 'admin_grant' });
      toast.success(`${u.email} → Founder/free access granted`);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Grant failed');
    } finally {
      setGrantingId(null);
    }
  };

  const handleRevokeFounder = async (u) => {
    setGrantingId(u.id);
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/founder/revoke`);
      toast.success(
        `${u.email} → Founder revoked (${data.restore_mode === 'restored_from_prior' ? 'prior state restored' : 'fresh 30-day trial'})`
      );
      setRevokeConfirm(null);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    } finally {
      setGrantingId(null);
    }
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-slate-500">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="admin-users-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-10 px-4 py-3 flex items-center justify-between safe-top min-h-[48px]">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/home')} className="text-slate-600 hover:text-slate-900" data-testid="admin-back">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>User Management</h1>
            <p className="text-xs text-slate-400">{users.length} user{users.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#235347] text-white rounded-lg text-sm font-medium hover:bg-[#1A3F36] transition-colors"
          data-testid="create-user-btn"
        >
          <UserPlus size={16} />
          Create User
        </button>
      </header>

      {/* Search */}
      <div className="px-6 py-3">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
            data-testid="admin-search-users"
          />
        </div>
      </div>

      {/* Users list */}
      <div className="flex-1 px-6 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={28} className="animate-spin text-[#235347]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users size={36} className="mx-auto text-slate-200 mb-3" weight="duotone" />
            <p className="text-sm text-slate-400">
              {search ? 'No users match your search' : 'No users yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <div
                key={u.id}
                className={`bg-white border rounded-xl p-4 transition-colors ${
                  u.active === false ? 'border-red-200 opacity-60' : 'border-slate-200'
                }`}
                data-testid="admin-user-card"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    u.role === 'admin' ? 'bg-[#235347]/10 text-[#235347]' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <UserCircle size={24} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-900 truncate">{u.name || 'No name'}</p>
                      {u.role === 'admin' && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-[#235347]/10 text-[#235347] rounded">
                          Admin
                        </span>
                      )}
                      {u.active === false && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-red-100 text-red-600 rounded">
                          Deactivated
                        </span>
                      )}
                      {/* Stage 3 — access state badge */}
                      {u.access_label && (() => {
                        const c = ACCESS_BADGE[u.access_label] || ACCESS_BADGE['No plan'];
                        return (
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${c.bg} ${c.text} ${c.border} inline-flex items-center gap-1`}
                            data-testid={`access-badge-${u.id}`}
                          >
                            {u.access_label === 'Founder/free' && <Crown size={10} weight="fill" />}
                            {u.has_stripe_customer && <CreditCard size={10} />}
                            {u.access_label}
                          </span>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <p className="text-[10px] text-slate-300">
                        Joined {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'unknown'}
                        {u.onboarding_completed ? ' · Onboarded' : ' · Not onboarded'}
                      </p>
                      {u.trial_end && u.access_label !== 'Founder/free' && (
                        <>
                          <span className="text-[10px] text-slate-200">·</span>
                          <span className="text-[10px] text-slate-400" data-testid={`trial-end-${u.id}`}>
                            Trial ends {formatTrialDate(u.trial_end)}
                          </span>
                        </>
                      )}
                      <span className="text-[10px] text-slate-200">·</span>
                      {(() => {
                        const activity = getActivityLabel(u.last_login_at);
                        return (
                          <span className={`text-[10px] font-medium ${activity.color}`} data-testid={`user-activity-${u.id}`}>
                            {activity.text}
                            {u.last_login_at && formatLastLogin(u.last_login_at) ? ` (${formatLastLogin(u.last_login_at)})` : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Founder access action (Stage 3) — visible for any non-self user */}
                  {u.email !== user?.email && (
                    u.foundational_granted ? (
                      <button
                        onClick={() => setRevokeConfirm(u)}
                        disabled={grantingId === u.id}
                        className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg text-rose-700 border border-rose-200 bg-white hover:bg-rose-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                        data-testid={`revoke-founder-${u.id}`}
                        title="Remove founder access"
                      >
                        {grantingId === u.id ? <Spinner size={12} className="animate-spin" /> : <X size={12} />}
                        Remove founder
                      </button>
                    ) : (
                      <button
                        onClick={() => handleGrantFounder(u)}
                        disabled={grantingId === u.id}
                        className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                        data-testid={`grant-founder-${u.id}`}
                        title="Grant founder/free access — removes 30-day restriction"
                      >
                        {grantingId === u.id ? <Spinner size={12} className="animate-spin" /> : <Crown size={12} weight="fill" />}
                        Grant founder
                      </button>
                    )
                  )}
                  {/* Don't allow deactivating self */}
                  {u.email !== user?.email && (
                    <button
                      onClick={() => handleToggleActive(u.id, u.active !== false)}
                      disabled={toggling === u.id}
                      className={`p-2 rounded-lg transition-colors ${
                        u.active !== false
                          ? 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      data-testid={`toggle-user-${u.id}`}
                      title={u.active !== false ? 'Deactivate user' : 'Activate user'}
                    >
                      {toggling === u.id ? (
                        <Spinner size={18} className="animate-spin" />
                      ) : u.active !== false ? (
                        <Prohibit size={18} />
                      ) : (
                        <CheckCircle size={18} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-3xl p-6 max-h-[80vh] overflow-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Create User
                </h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
                    data-testid="create-user-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="user@example.com"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
                    data-testid="create-user-email"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
                    data-testid="create-user-password"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
                    data-testid="create-user-role"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full mt-6 bg-[#235347] text-white rounded-full py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="create-user-submit"
              >
                {creating ? <Spinner size={18} className="animate-spin" /> : <UserPlus size={18} />}
                {creating ? 'Creating...' : 'Create User'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Revoke Founder confirmation (Stage 3) */}
      <AnimatePresence>
        {revokeConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center"
            onClick={() => setRevokeConfirm(null)}
            data-testid="revoke-confirm-overlay"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-3xl p-6"
            >
              <h2 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Remove founder access?
              </h2>
              <p className="text-sm text-slate-600 mb-1">
                <span className="font-medium">{revokeConfirm.email}</span>
              </p>
              <p className="text-sm text-slate-500 mb-5">
                {revokeConfirm.prior_plan_type
                  ? `Will restore prior plan: ${revokeConfirm.prior_plan_type}${revokeConfirm.prior_trial_end ? ` (trial ends ${formatTrialDate(revokeConfirm.prior_trial_end)})` : ''}.`
                  : 'No prior plan saved — user will receive a fresh 30-day trial as a fallback.'}
                {' '}Stripe customer/subscription IDs are preserved.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setRevokeConfirm(null)}
                  disabled={grantingId === revokeConfirm.id}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  data-testid="revoke-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRevokeFounder(revokeConfirm)}
                  disabled={grantingId === revokeConfirm.id}
                  className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="revoke-confirm-btn"
                >
                  {grantingId === revokeConfirm.id ? <Spinner size={14} className="animate-spin" /> : null}
                  Remove founder access
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsersPage;
